/**
 * 27/10/2026 — All on · All off beside the containers' tally: every container on the board (the
 * open campaign group's) switched in one click; each is off when there is nothing to switch.
 * All on asks first when some would auto-send real SMS today.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Power, PowerOff } from 'lucide-react';

import { Button, ConfirmDialog } from '@/components/ui';

import type { DropGenContainerSummary } from '../../../drop-generator.types';
import type { LiveAutoSend } from '../../shared/StateBadges';
import { useSwitchAll } from '../actions/use-switch-all';

interface Props {
  planId: string;
  /** The containers on the board. */
  containers: DropGenContainerSummary[];
  autoSendOf: (id: string) => LiveAutoSend;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Why All on asks. */
export const liveLine = (live: number) =>
  `${live === 1 ? '1 of them auto-sends' : `${live} of them auto-send`} real SMS today — once on, their drops go out by themselves.`;

export const SwitchAll: FC<Props> = ({ planId, containers, autoSendOf }) => {
  const s = useSwitchAll(planId, containers, autoSendOf);
  const off = containers.filter((c) => !c.enabled).length;
  const on = containers.length - off;
  const pill = 'h-7 gap-1 px-2 text-xs';
  return (
    <span className="inline-flex items-center rounded-md border border-border/70 bg-muted/30 p-0.5">
      <Button type="button" size="sm" variant="ghost" className={pill} disabled={off === 0 || s.busy} title={off ? `Switch on the ${plural(off, 'container')} that are off` : 'Every container is on'} onClick={s.allOn}>
        <Power className="h-3.5 w-3.5 text-success" aria-hidden /> All on
      </Button>
      <Button type="button" size="sm" variant="ghost" className={pill} disabled={on === 0 || s.busy} title={on ? `Switch off the ${plural(on, 'container')} that are on` : 'Every container is off'} onClick={s.allOff}>
        <PowerOff className="h-3.5 w-3.5 text-muted-foreground" aria-hidden /> All off
      </Button>
      <ConfirmDialog
        open={s.asking !== null}
        title={`Switch on ${plural(s.asking?.ids.length ?? 0, 'container')}?`}
        description={liveLine(s.asking?.live ?? 0)}
        confirmLabel="Switch them on"
        busy={s.busy}
        onConfirm={s.confirm}
        onCancel={s.cancel}
      />
    </span>
  );
};
